import React, { useState } from "react";
import { ref, push, set, runTransaction } from "firebase/database";
import { database, auth } from "../firebaseConfig";
import toast from "react-hot-toast";

/**
 * AccountTransactionModal — improved visuals
 * - writes to Companies/{companyKey}/Accounts/{accountType}/{year}/{month}/{entryId}
 * - updates Companies/{companyKey}/AccountBalances/{accountType} via runTransaction
 */
const AccountTransactionModal = ({ companyKey, accountType, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("credit");
  const [type, setType] = useState("adjustment");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const currentUser = auth.currentUser;

  const handleSave = async () => {
    if (!companyKey || !accountType) return;
    const amt = Number(amount || 0);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setSaving(true);
    try {
      const now = new Date();
      const y = String(now.getFullYear());
      const m = String(now.getMonth() + 1);
      const keyRef = push(ref(database));
      const key = keyRef.key;
      const entry = {
        id: key,
        date: now.toISOString(),
        createdAt: Date.now(),
        amount: amt,
        direction,
        type,
        notes,
        reference: `TX-${key}`,
        counterparty: "",
        createdBy: currentUser?.email || currentUser?.uid || "unknown",
      };
      const path = `Companies/${companyKey}/Accounts/${accountType}/${y}/${m}/${key}`;
      await set(ref(database, path), entry);

      const balRef = ref(database, `Companies/${companyKey}/AccountBalances/${accountType}`);
      await runTransaction(balRef, (cur) => {
        const delta = (direction === "credit" ? 1 : -1) * amt;
        if (cur === null) return { balance: delta, updatedAt: Date.now() };
        return { balance: Number(cur.balance || 0) + delta, updatedAt: Date.now() };
      });

      toast.success("Transaction saved");
      onSaved && onSaved(entry);
    } catch (err) {
      console.error("save tx", err);
      toast.error("Failed to save transaction");
    } finally {
      setSaving(false);
      onClose && onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-cyan-700 mb-3">Add transaction — {accountType}</h3>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600">Amount</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border rounded px-3 py-2" />
          </div>

          <div>
            <label className="text-xs text-gray-600">Direction</label>
            <select value={direction} onChange={(e) => setDirection(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="credit">Credit (increase)</option>
              <option value="debit">Debit (decrease)</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Type</label>
            <input value={type} onChange={(e) => setType(e.target.value)} className="w-full border rounded px-3 py-2" />
          </div>

          <div>
            <label className="text-xs text-gray-600">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-3 py-2" />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 border rounded" disabled={saving}>Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-cyan-600 text-white rounded" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountTransactionModal;